import { Component, ElementRef, OnDestroy, OnInit, QueryList, ViewChildren } from '@angular/core';
import { ActivatedRoute, Router } from "@angular/router";
import { Subject } from 'rxjs';
import { finalize, takeUntil, timeout } from 'rxjs/operators';
import { UserIdRequest } from 'src/app/models/login.models';
import { AlertService } from 'src/app/services/alert.service';
import { HttpService } from "../../services/http.service";
import { MitraGuard } from 'src/app/services/mitra.guard';


@Component({
  selector: 'app-login-mitra',
  templateUrl: './login-mitra.component.html',
  styleUrls: ['./login-mitra.component.scss']
})
export class LoginMitraComponent implements OnInit, OnDestroy {
  @ViewChildren('otpInput') otpInputs!: QueryList<ElementRef<HTMLInputElement>>;

  private readonly baseUrl: string = "/api/v1/wp/mitra/auth/";
  private destroy$ = new Subject<void>();
  public username: string | null = null;
  private generatedCode: string | null = null;

  otpDigits: string[] = ['', '', '', '', '', ''];
  otpError: string | null = null;
  otpExpired = false;
  otpCountdown = 0;
  private otpInterval: ReturnType<typeof setInterval> | null = null;
  readonly OTP_DURATION = 120;

  public isCodeSent: boolean = false;
  public loading: boolean = false;
  public verifying: boolean = false;

  constructor(
    private alert: AlertService,
    private http: HttpService,
    private router: Router,
    private route: ActivatedRoute
  ) { }

  ngOnInit(): void {
    const token = typeof localStorage !== 'undefined' ? localStorage.getItem('token') : null;
    if (token) {
      if (token === MitraGuard.cachedToken && MitraGuard.cachedUserId) {
        this.router.navigate(['/mitra', MitraGuard.cachedUserId]);
        return;
      }

      this.http.get('/api/v1/wp/mitra/auth/get-user-id')
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (res: any) => {
            const userId = res?.data?.user_id;
            if (userId) {
              MitraGuard.cachedToken = token;
              MitraGuard.cachedUserId = String(userId);
              this.router.navigate(['/mitra', userId]);
            }
          },
          error: () => {
            localStorage.removeItem('token');
            MitraGuard.clearCache();
          }
        });
      return;
    }

    const usernameFromUrl = this.route.snapshot.queryParamMap.get('username');

    if (usernameFromUrl) {
      this.username = usernameFromUrl.trim();
      this.sendCode();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.clearOtpTimer();
  }

  sendCode(): void {
    if (this.loading) return;

    const cleanUsername = this.username?.trim();
    if (!cleanUsername) {
      this.alert.warning(
        "Masukkan username anda",
        {
          title: 'Username Diperlukan',
        },
      );
      return;
    }

    const payload: UserIdRequest = {
      user_id: cleanUsername
    };

    this.loading = true;

    this.http.post(this.baseUrl + 'validate-user', payload)
      .pipe(
        timeout(30000),
        takeUntil(this.destroy$),
        finalize(() => (this.loading = false))
      )
      .subscribe({
        next: (res: any) => {
          const data = res.data;

          this.generatedCode = data.code;
          this.isCodeSent = true;
          this.resetOtpState();
          this.startOtpTimer();
          setTimeout(() => this.focusInput(0), 100);
        },
        error: (err) => {
          const message = err?.error?.responseMessage ?? 'Terjadi kesalahan pada sistem. Mohon maaf atas ketidaknyamanannya';

          if (err.status === 400 || err.status === 403) {
            this.alert.warning(
              message,
              {
                title: 'Akses Ditolak',
                onClose: () => {
                  this.reset();
                }
              }
            );
            return;
          }
          this.alert.error(
            message,
            {
              title: 'Terjadi Kesalahan Sistem',
              onClose: () => {
                this.reset();
              }
            }
          );
        }
      });
  }

  reset(): void {
    this.clearOtpTimer();
    this.isCodeSent = false;
    this.username = null;
    this.generatedCode = null;
    this.verifying = false;
    this.loading = false;
    this.resetOtpState();
  }

  private loginWithOtp(token: string): void {
    this.verifying = true;

    const payload = {
      nik: String(this.username).trim(),
      token_access_user: this.generatedCode,
      token_code: token,
    };

    this.http.post(this.baseUrl + 'login', payload)
      .pipe(
        timeout(30000),
        takeUntil(this.destroy$),
        finalize(() => (this.verifying = false))
      )
      .subscribe({
        next: (res: any) => {
          const data = res.data;
          MitraGuard.clearCache();
          localStorage.removeItem('mitra_is_inactive');
          localStorage.setItem('token', data.token);

          const targetId = data?.user_id || this.username?.trim();
          this.router.navigate(['/mitra', targetId]).then(() => {
            this.reset();
            this.alert.successAuto(
              'Login Berhasil',
              { title: 'Success' }
            );
          });
        },
        error: (err) => {
          if ([400, 403].includes(err.status)) {
            this.otpError = err?.error?.responseMessage ?? 'Kode OTP salah. Coba lagi';
          } else {
            this.otpError = 'Login gagal. Coba lagi';
          }
          this.otpDigits = ['', '', '', '', '', ''];
          this.focusInput(0);
        }
      });
  }

  private checkOtpComplete(): void {
    const otp = this.otpDigits.join('');

    if (otp.length < 6) {
      this.otpError = null;
      return;
    }

    if (this.otpExpired) return;

    this.otpError = null;

    setTimeout(() => this.loginWithOtp(otp), 200);
  }

  get otpTimerDisplay(): string {
    const minutes = Math.floor(this.otpCountdown / 60);
    const seconds = this.otpCountdown % 60;

    return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
  }

  private focusInput(index: number): void {
    const inputElements = this.otpInputs?.toArray();
    if (inputElements && inputElements[index]) {
      inputElements[index].nativeElement.focus();
    }
  }

  onOtpInput(event: Event, index: number): void {
    if (this.otpExpired) return;

    const input = event.target as HTMLInputElement;
    const clean = input.value.replace(/[^0-9]/g, '');

    input.value = clean;

    if (!clean) {
      this.otpDigits[index] = '';
      return;
    }

    const digit = clean.slice(-1);
    this.otpDigits[index] = digit;
    input.value = digit;

    if (index < 5) {
      this.focusInput(index + 1);
    }

    this.checkOtpComplete();
  }

  onOtpKeyDown(event: KeyboardEvent, index: number): void {
    if (event.ctrlKey || event.metaKey) return;

    const allowedKeys = [
      'Backspace',
      'Tab',
      'ArrowLeft',
      'ArrowRight',
      'Delete'
    ];

    if (event.key === 'Backspace') {
      this.otpError = null;

      if (!this.otpDigits[index] && index > 0) {
        this.focusInput(index - 1);
      }

      return;
    }

    if (!/^[0-9]$/.test(event.key) && !allowedKeys.includes(event.key)) {
      event.preventDefault();
    }
  }

  onOtpPaste(event: ClipboardEvent): void {
    if (this.otpExpired) return;

    const paste = event.clipboardData?.getData('text') ?? '';
    const digits = paste.replace(/\D/g, '').slice(0, 6);

    if (!digits) return;

    for (let i = 0; i < 6; i++) {
      this.otpDigits[i] = digits[i] || '';
    }

    this.focusInput(Math.min(digits.length, 5));
    this.checkOtpComplete();

    event.preventDefault();
  }

  trackByIndex(index: number): number {
    return index;
  }

  private startOtpTimer(): void {
    this.clearOtpTimer();
    this.otpExpired = false;
    this.otpCountdown = this.OTP_DURATION;

    this.otpInterval = setInterval(() => {
      this.otpCountdown--;

      if (this.otpCountdown <= 0) {
        this.clearOtpTimer();
        this.otpDigits = ['', '', '', '', '', ''];
        this.otpExpired = true;
        this.otpError = 'Kode OTP sudah kadaluarsa';
      }
    }, 1000);
  }

  private clearOtpTimer(): void {
    if (this.otpInterval) {
      clearInterval(this.otpInterval);
      this.otpInterval = null;
    }
  }

  private resetOtpState(): void {
    this.otpDigits = ['', '', '', '', '', ''];
    this.otpError = null;
    this.otpExpired = false;
  }

  resendOtp(): void {
    if (this.loading || this.otpCountdown > 0) return;

    this.resetOtpState();
    this.sendCode();
  }

  cancelOtp(): void {
    this.clearOtpTimer();
    this.reset();
  }
}
